import { IconCertificate } from "@tabler/icons-react"
import { type FC, useState } from "react"
import styled from "styled-components"

import { type PersonalizedThinCourse } from "@forento/core/models/course"
import { toPriceString } from "@forento/core/utilities/currency"

import Link from "~/components/Link"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc from "~/utilities/trpc"

import { AccessButton, containerClasses, Tag, tagsClasses, titleClasses } from "./shared"

type Props = {
	course: PersonalizedThinCourse & { certificate: { id: number; createDate: Date } | null }
	userId: number
	reload(): Promise<unknown>
}
const Course: FC<Props> = ({ course, userId, reload }) => {
	const hasAssignProductAccess = usePlatform()!.plan.access.assignProducts

	const [isSubmittingAccess, setSubmittingAccess] = useState(false)

	async function handleGiveAccess() {
		setSubmittingAccess(true)
		try {
			await trpc.product.giveManualAccess.mutate({ product: { type: "course", id: course.id }, userId })
			await reload()
		} catch (error) {
			console.error(error)
			setSubmittingAccess(false)
		}
		setSubmittingAccess(false)
	}

	async function handleRemoveAccess() {
		setSubmittingAccess(true)
		try {
			await trpc.product.removeManualAccess.mutate({ product: { type: "course", id: course.id }, userId })
			await reload()
		} catch (error) {
			console.error(error)
			setSubmittingAccess(false)
		}
		setSubmittingAccess(false)
	}

	return (
		<div className={containerClasses}>
			<div className="flex flex-1 flex-col gap-2">
				<h3 className={titleClasses}>{course.title}</h3>
				{course.status === "unlocked" && (
					<ProgressBar>
						<ProgressFill $progress={course.progress} />
					</ProgressBar>
				)}
			</div>
			<div className={tagsClasses}>
				{course.certificate && (
					<Link
						to={routes.user.certificate(userId, course.certificate.id)}
						className="bg-primary flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-sm leading-none font-semibold text-white transition-all hover:-translate-y-px hover:shadow-lg"
					>
						<IconCertificate className="size-4" />
						View certificate
					</Link>
				)}
				{course.status === "unlocked" ? (
					<>
						{course.unlockedCause === "purchased" &&
							course.order.type === "external" &&
							course.order.createdBy === "manual" && (
								<AccessButton isDanger onClick={handleRemoveAccess} isLoading={isSubmittingAccess}>
									Remove access
								</AccessButton>
							)}
						<Tag color="orange">
							{course.progress === 1 ? "Completed" : `${Math.floor(course.progress * 100)}%`}
						</Tag>
						{course.unlockedCause === "tester" ? (
							<Tag color="blue">Tester</Tag>
						) : course.unlockedCause === "purchased" ? (
							course.order.type === "external" ? (
								course.order.createdBy === "zapier" ? (
									<Tag color="blue">Purchased through Zapier</Tag>
								) : (
									<Tag color="blue">Manual access</Tag>
								)
							) : (
								<Tag color="blue">Purchased</Tag>
							)
						) : course.unlockedCause === "membership" ? (
							<Tag color="blue">Membership</Tag>
						) : null}
					</>
				) : (
					hasAssignProductAccess && (
						<AccessButton onClick={handleGiveAccess} isLoading={isSubmittingAccess}>
							Give access
						</AccessButton>
					)
				)}
				<Tag color="green">
					{course.isManualOnly
						? "Manual only"
						: course.price !== null
							? toPriceString(course.price)
							: course.membershipIds.length > 0
								? "Membership"
								: "Free"}
				</Tag>
			</div>
		</div>
	)
}

const ProgressBar = styled.div`
	width: 100%;
	height: 4px;
	background-color: #f3f4f6;
	border-radius: 2px;
	overflow: hidden;
`

const ProgressFill = styled.div<{ $progress: number }>`
	height: 100%;
	background: linear-gradient(90deg, #10b981 0%, #059669 100%);
	width: ${props => props.$progress * 100}%;
	transition: width 0.3s ease;
`

export default Course
